import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { 
  PlusIcon, 
  EyeIcon, 
  XMarkIcon, 
  PhotoIcon, 
  TrashIcon,
  CloudArrowUpIcon,
  ArrowUpTrayIcon,
  MapPinIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  CalendarIcon,
  ClockIcon,
  CheckIcon,
  CurrencyDollarIcon,
  ArchiveBoxIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';

// Avatar color palette
const avatarColors = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'];

const getAvatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const getInitials = (name) => {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Client Picker Modal Component
const ClientPicker = ({ clients, selectedClientId, onSelect, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const sectionRefs = useRef({});
  
  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    return clients.filter(client => 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (client.phone && client.phone.includes(searchTerm))
    );
  }, [clients, searchTerm]);

  const groupedClients = useMemo(() => {
    const sorted = [...filteredClients].sort((a, b) => 
      a.name.localeCompare(b.name)
    );
    
    const groups = {};
    sorted.forEach(client => {
      const firstChar = client.name[0]?.toUpperCase() || '#';
      const key = /[A-Z]/.test(firstChar) ? firstChar : '#';
      if (!groups[key]) groups[key] = [];
      groups[key].push(client);
    });
    
    return groups;
  }, [filteredClients]);

  const alphabet = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#'];
  const availableLetters = new Set(Object.keys(groupedClients));

  const scrollToSection = (letter) => {
    if (sectionRefs.current[letter]) {
      sectionRefs.current[letter].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        background: 'rgba(0,0,0,0.5)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 1100,
        padding: 20
      }}
    >
      <div 
        className="modal-content" 
        onClick={e => e.stopPropagation()}
        style={{ 
          background: 'white', 
          borderRadius: 12, 
          width: '100%', 
          maxWidth: 500, 
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Select Client</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <XMarkIcon style={{ width: 24, height: 24 }} />
            </button>
          </div>
          
          {/* Search Bar */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            background: '#f1f5f9', 
            borderRadius: 10, 
            padding: '8px 12px',
            gap: 8
          }}>
            <MagnifyingGlassIcon style={{ width: 20, height: 20, color: '#64748b' }} />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                flex: 1, 
                border: 'none', 
                background: 'transparent', 
                outline: 'none',
                fontSize: 16
              }}
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                style={{ background: '#cbd5e1', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <XMarkIcon style={{ width: 12, height: 12, color: '#64748b' }} />
              </button>
            )}
          </div>
        </div>

        {/* Client List with Alphabet Index */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Client List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 0 0' }}>
            {Object.keys(groupedClients).sort().map(letter => (
              <div key={letter} ref={el => sectionRefs.current[letter] = el}>
                {/* Section Header */}
                <div style={{ 
                  background: '#f8fafc', 
                  padding: '6px 20px', 
                  fontSize: 13, 
                  fontWeight: 600, 
                  color: '#64748b',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  {letter}
                </div>
                
                {/* Clients in Section */}
                {groupedClients[letter].map(client => (
                  <div
                    key={client.id}
                    onClick={() => {
                      onSelect(client);
                      onClose();
                    }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '12px 20px',
                      cursor: 'pointer',
                      background: selectedClientId === client.id ? '#e0f2fe' : 'white',
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedClientId !== client.id) {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedClientId !== client.id) {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    {/* Avatar */}
                    <div style={{ 
                      width: 44, 
                      height: 44, 
                      borderRadius: '50%', 
                      background: getAvatarColor(client.name),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 12,
                      flexShrink: 0
                    }}>
                      <span style={{ color: 'white', fontSize: 16, fontWeight: 600 }}>
                        {getInitials(client.name)}
                      </span>
                    </div>
                    
                    {/* Name and Address */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 15, color: '#1f2937' }}>
                        {client.name}
                      </div>
                      {client.address && (
                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {client.address}
                        </div>
                      )}
                    </div>
                    
                    {/* Checkmark if selected */}
                    {selectedClientId === client.id && (
                      <CheckIcon style={{ width: 20, height: 20, color: '#2B7FE0', marginLeft: 8 }} />
                    )}
                  </div>
                ))}
              </div>
            ))}
            
            {filteredClients.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                No clients found
              </div>
            )}
          </div>

          {/* Alphabet Index */}
          <div style={{ 
            width: 24, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 0',
            background: '#f8fafc'
          }}>
            {alphabet.map(letter => (
              <div
                key={letter}
                onClick={() => scrollToSection(letter)}
                style={{ 
                  fontSize: 10,
                  fontWeight: 600,
                  color: availableLetters.has(letter) ? '#2B7FE0' : '#cbd5e1',
                  cursor: availableLetters.has(letter) ? 'pointer' : 'default',
                  padding: '2px 4px',
                  borderRadius: 2,
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (availableLetters.has(letter)) {
                    e.currentTarget.style.background = '#e0f2fe';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {letter}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceCalls = () => {
  const { user, isAdmin } = useAuth();
  const [serviceCalls, setServiceCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [showClientPicker, setShowClientPicker] = useState(false);
  
  // Tab state - 'active' or 'archived'
  const [activeTab, setActiveTab] = useState('active');
  
  // Date/Time editing states
  const [isEditingDateTime, setIsEditingDateTime] = useState(false);
  const [editedDate, setEditedDate] = useState('');
  const [editedTime, setEditedTime] = useState('');
  const [savingDateTime, setSavingDateTime] = useState(false);
  
  // Cost editing states
  const [isEditingCost, setIsEditingCost] = useState(false);
  const [editedCost, setEditedCost] = useState('');
  const [savingCost, setSavingCost] = useState(false);
  
  const [formData, setFormData] = useState({
    client_id: '',
    assigned_employees: [],
    title: '',
    description: '',
    scheduled_date: moment().format('YYYY-MM-DD'),
    scheduled_time: '',
    priority: 'normal',
    cost: '',
    notes: ''
  });

  useEffect(() => {
    fetchServiceCalls();
    fetchClients();
    fetchUsers();
  }, []);

  const fetchServiceCalls = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-calls`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServiceCalls(data.serviceCalls || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPhotos = async (serviceCallId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/photos/service-call/${serviceCallId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPhotos(data.photos || []);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  // Filter service calls based on active tab
  const filteredServiceCalls = useMemo(() => {
    if (activeTab === 'active') {
      // Active: pending, assigned, in-progress
      return serviceCalls.filter(call => 
        ['pending', 'assigned', 'in-progress'].includes(call.status)
      );
    } else {
      // Archived: completed, cancelled
      return serviceCalls.filter(call => 
        ['completed', 'cancelled'].includes(call.status)
      );
    }
  }, [serviceCalls, activeTab]);

  // Count for each tab
  const activeCounts = useMemo(() => {
    const active = serviceCalls.filter(call => 
      ['pending', 'assigned', 'in-progress'].includes(call.status)
    ).length;
    const archived = serviceCalls.filter(call => 
      ['completed', 'cancelled'].includes(call.status)
    ).length;
    return { active, archived };
  }, [serviceCalls]);

  const viewDetails = async (call) => {
    // Fetch full details including assigned_employees
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-calls/${call.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const fullCall = await response.json();
        setSelectedCall(fullCall);
        setEditedDate(fullCall.scheduled_date || '');
        setEditedTime(fullCall.scheduled_time || '');
        setEditedCost(fullCall.cost || '');
      } else {
        setSelectedCall(call);
        setEditedDate(call.scheduled_date || '');
        setEditedTime(call.scheduled_time || '');
        setEditedCost(call.cost || '');
      }
    } catch (error) {
      setSelectedCall(call);
      setEditedDate(call.scheduled_date || '');
      setEditedTime(call.scheduled_time || '');
      setEditedCost(call.cost || '');
    }
    setIsEditingDateTime(false);
    setIsEditingCost(false);
    setShowDetailModal(true);
    await fetchPhotos(call.id);
  };

  const openNavigation = (address) => {
    if (!address) {
      toast.warning('No address available');
      return;
    }
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  };

  const handlePhotoUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token');

    try {
      // Upload files one by one
      for (const file of files) {
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('photo_type', 'during');
        formData.append('caption', '');

        const response = await fetch(`${API_URL}/api/photos/service-call/${selectedCall.id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to upload photo');
        }
      }

      toast.success(`${files.length} photo(s) uploaded successfully!`);
      await fetchPhotos(selectedCall.id);
    } catch (error) {
      toast.error('Failed to upload photos');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/photos/service-call/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Photo deleted');
        await fetchPhotos(selectedCall.id);
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      toast.error('Error deleting photo');
    }
  };

  const deleteServiceCall = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service call? This will also delete all associated photos.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-calls/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Service call deleted');
        setShowDetailModal(false);
        fetchServiceCalls();
      } else {
        toast.error('Failed to delete service call');
      }
    } catch (error) {
      toast.error('Error deleting service call');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-calls`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Service call created!');
        setShowModal(false);
        resetForm();
        fetchServiceCalls();
      } else {
        toast.error('Failed to create service call');
      }
    } catch (error) {
      toast.error('Error creating service call');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      assigned_employees: [],
      title: '',
      description: '',
      scheduled_date: moment().format('YYYY-MM-DD'),
      scheduled_time: '',
      priority: 'normal',
      cost: '',
      notes: ''
    });
  };

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-calls/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        toast.success('Status updated');
        fetchServiceCalls();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const saveDateTime = async () => {
    if (!selectedCall) return;
    
    setSavingDateTime(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-calls/${selectedCall.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          scheduled_date: editedDate,
          scheduled_time: editedTime || null
        })
      });
      
      if (response.ok) {
        toast.success('Date/time updated');
        setSelectedCall({
          ...selectedCall,
          scheduled_date: editedDate,
          scheduled_time: editedTime
        });
        setIsEditingDateTime(false);
        fetchServiceCalls();
      } else {
        toast.error('Failed to update date/time');
      }
    } catch (error) {
      toast.error('Error updating date/time');
    } finally {
      setSavingDateTime(false);
    }
  };

  const saveCost = async () => {
    if (!selectedCall) return;
    
    setSavingCost(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-calls/${selectedCall.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          cost: editedCost ? parseFloat(editedCost) : null
        })
      });
      
      if (response.ok) {
        toast.success('Cost updated');
        setSelectedCall({
          ...selectedCall,
          cost: editedCost
        });
        setIsEditingCost(false);
        fetchServiceCalls();
      } else {
        toast.error('Failed to update cost');
      }
    } catch (error) {
      toast.error('Error updating cost');
    } finally {
      setSavingCost(false);
    }
  };

  const toggleEmployeeSelection = (userId) => {
    setFormData(prev => {
      const isSelected = prev.assigned_employees.includes(userId);
      return {
        ...prev,
        assigned_employees: isSelected
          ? prev.assigned_employees.filter(id => id !== userId)
          : [...prev.assigned_employees, userId]
      };
    });
  };

  // Helper function to format date without timezone shift
  const formatDate = (dateStr, format = 'MM/DD/YYYY') => {
    // Use moment.utc to prevent timezone conversion that shifts the day
    return moment.utc(dateStr).format(format);
  };

  // Format cost as currency
  const formatCost = (cost) => {
    if (!cost && cost !== 0) return 'Not set';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cost);
  };

  // Helper to get assigned employees display string
  const getAssignedDisplay = (call) => {
    if (call.assigned_employees && call.assigned_employees.length > 0) {
      if (call.assigned_employees.length === 1) {
        return call.assigned_employees[0].name;
      }
      return `${call.assigned_employees.length} employees`;
    }
    return call.assigned_to_name || 'Unassigned';
  };

  // Get selected client name for the picker button
  const getSelectedClientName = () => {
    if (!formData.client_id) return null;
    const client = clients.find(c => c.id === formData.client_id);
    return client ? client.name : null;
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600 }}>Service Calls</h1>
        {isAdmin && (
          <button 
            onClick={() => setShowModal(true)}
            className="btn btn-primary"
            style={{ background: '#2B7FE0', color: 'white', padding: '10px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <PlusIcon style={{ width: 20, height: 20 }} />
            Create Service Call
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e2e8f0' }}>
        <button
          onClick={() => setActiveTab('active')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'active' ? '3px solid #2B7FE0' : '3px solid transparent',
            marginBottom: -2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 15,
            fontWeight: activeTab === 'active' ? 600 : 400,
            color: activeTab === 'active' ? '#2B7FE0' : '#64748b',
            transition: 'all 0.2s'
          }}
        >
          <ClipboardDocumentListIcon style={{ width: 20, height: 20 }} />
          Active
          <span style={{
            background: activeTab === 'active' ? '#2B7FE0' : '#e2e8f0',
            color: activeTab === 'active' ? 'white' : '#64748b',
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600
          }}>
            {activeCounts.active}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('archived')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'archived' ? '3px solid #2B7FE0' : '3px solid transparent',
            marginBottom: -2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 15,
            fontWeight: activeTab === 'archived' ? 600 : 400,
            color: activeTab === 'archived' ? '#2B7FE0' : '#64748b',
            transition: 'all 0.2s'
          }}
        >
          <ArchiveBoxIcon style={{ width: 20, height: 20 }} />
          Completed/Archived
          <span style={{
            background: activeTab === 'archived' ? '#2B7FE0' : '#e2e8f0',
            color: activeTab === 'archived' ? 'white' : '#64748b',
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600
          }}>
            {activeCounts.archived}
          </span>
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {filteredServiceCalls.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              {activeTab === 'active' ? (
                <p>No active service calls. Click "Create Service Call" to add one!</p>
              ) : (
                <p>No completed or archived service calls yet.</p>
              )}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Client</th>
                  <th>Title</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                  <th>Priority</th>
                  {activeTab === 'archived' && <th>Cost</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServiceCalls.map(call => (
                  <tr key={call.id}>
                    <td>{formatDate(call.scheduled_date)}</td>
                    <td>{call.scheduled_time || 'TBD'}</td>
                    <td>{call.client_name}</td>
                    <td>{call.title}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {call.assigned_employees && call.assigned_employees.length > 1 && (
                          <UserGroupIcon style={{ width: 16, height: 16, color: '#2B7FE0' }} />
                        )}
                        {getAssignedDisplay(call)}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge status-${call.status}`}>
                        {call.status}
                      </span>
                    </td>
                    <td>
                      <span className={`priority-badge priority-${call.priority}`}>
                        {call.priority}
                      </span>
                    </td>
                    {activeTab === 'archived' && (
                      <td style={{ fontWeight: 500, color: call.cost ? '#16a34a' : '#9ca3af' }}>
                        {call.cost ? formatCost(call.cost) : '-'}
                      </td>
                    )}
                    <td>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                          onClick={() => viewDetails(call)}
                          style={{
                            padding: '6px 12px',
                            background: '#2B7FE0',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 13
                          }}
                        >
                          <EyeIcon style={{ width: 14, height: 14 }} />
                          View
                        </button>
                        <select 
                          value={call.status}
                          onChange={(e) => updateStatus(call.id, e.target.value)}
                          className="form-select"
                          style={{ width: 'auto', padding: '6px 8px', borderRadius: 4, fontSize: 13 }}
                        >
                          <option value="pending">Pending</option>
                          <option value="assigned">Assigned</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail Modal with Photos */}
      {showDetailModal && selectedCall && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ padding: 24, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h2 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>{selectedCall.title}</h2>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span className={`status-badge status-${selectedCall.status}`}>
                    {selectedCall.status}
                  </span>
                  <span className={`priority-badge priority-${selectedCall.priority}`}>
                    {selectedCall.priority}
                  </span>
                </div>
              </div>
              <button onClick={() => setShowDetailModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: 24 }}>
              {/* Service Call Info */}
              <div style={{ marginBottom: 32 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Service Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Client</label>
                    <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{selectedCall.client_name}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Assigned To</label>
                    {selectedCall.assigned_employees && selectedCall.assigned_employees.length > 0 ? (
                      <div style={{ marginTop: 4 }}>
                        {selectedCall.assigned_employees.map((emp, idx) => (
                          <span 
                            key={emp.user_id || idx}
                            style={{
                              display: 'inline-block',
                              background: '#e0f2fe',
                              color: '#0369a1',
                              padding: '4px 10px',
                              borderRadius: 16,
                              fontSize: 13,
                              marginRight: 6,
                              marginBottom: 4
                            }}
                          >
                            {emp.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{selectedCall.assigned_to_name || 'Unassigned'}</p>
                    )}
                  </div>
                  
                  {/* Scheduled Date with Edit */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Scheduled Date</label>
                      {isAdmin && !isEditingDateTime && (
                        <button
                          onClick={() => setIsEditingDateTime(true)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                          title="Edit date/time"
                        >
                          <PencilIcon style={{ width: 14, height: 14, color: '#2B7FE0' }} />
                        </button>
                      )}
                    </div>
                    {isEditingDateTime ? (
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalendarIcon style={{ width: 16, height: 16, color: '#64748b' }} />
                        <input
                          type="date"
                          value={editedDate}
                          onChange={(e) => setEditedDate(e.target.value)}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '2px solid #2B7FE0', fontSize: 14 }}
                        />
                      </div>
                    ) : (
                      <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{formatDate(selectedCall.scheduled_date, 'MMMM D, YYYY')}</p>
                    )}
                  </div>
                  
                  {/* Time with Edit */}
                  <div>
                    <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Time</label>
                    {isEditingDateTime ? (
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ClockIcon style={{ width: 16, height: 16, color: '#64748b' }} />
                        <input
                          type="time"
                          value={editedTime}
                          onChange={(e) => setEditedTime(e.target.value)}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '2px solid #2B7FE0', fontSize: 14 }}
                        />
                      </div>
                    ) : (
                      <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{selectedCall.scheduled_time || 'TBD'}</p>
                    )}
                  </div>
                </div>

                {/* Save/Cancel buttons for date/time editing */}
                {isEditingDateTime && (
                  <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                    <button
                      onClick={saveDateTime}
                      disabled={savingDateTime}
                      style={{
                        padding: '8px 16px',
                        background: '#2B7FE0',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: savingDateTime ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 14,
                        fontWeight: 500,
                        opacity: savingDateTime ? 0.7 : 1
                      }}
                    >
                      <CheckIcon style={{ width: 16, height: 16 }} />
                      {savingDateTime ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingDateTime(false);
                        setEditedDate(selectedCall.scheduled_date || '');
                        setEditedTime(selectedCall.scheduled_time || '');
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#e2e8f0',
                        color: '#1f2937',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 500
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Cost Section with Edit (Admin Only) */}
                {isAdmin && (
                  <div style={{ marginTop: 16, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <CurrencyDollarIcon style={{ width: 18, height: 18, color: '#16a34a' }} />
                      <label style={{ fontSize: 13, color: '#166534', fontWeight: 600 }}>Cost</label>
                      {!isEditingCost && (
                        <button
                          onClick={() => setIsEditingCost(true)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                          title="Edit cost"
                        >
                          <PencilIcon style={{ width: 14, height: 14, color: '#16a34a' }} />
                        </button>
                      )}
                    </div>
                    {isEditingCost ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 16, color: '#166534' }}>$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={editedCost}
                            onChange={(e) => setEditedCost(e.target.value)}
                            placeholder="0.00"
                            style={{ padding: '6px 10px', borderRadius: 6, border: '2px solid #16a34a', fontSize: 14, width: 120 }}
                          />
                        </div>
                        <button
                          onClick={saveCost}
                          disabled={savingCost}
                          style={{
                            padding: '6px 12px',
                            background: '#16a34a',
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: savingCost ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 13,
                            fontWeight: 500,
                            opacity: savingCost ? 0.7 : 1
                          }}
                        >
                          <CheckIcon style={{ width: 14, height: 14 }} />
                          {savingCost ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingCost(false);
                            setEditedCost(selectedCall.cost || '');
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#e2e8f0',
                            color: '#1f2937',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <p style={{ fontSize: 18, color: '#166534', fontWeight: 600, margin: 0 }}>
                        {formatCost(selectedCall.cost)}
                      </p>
                    )}
                  </div>
                )}

                {/* Client Address with Navigate Button */}
                {selectedCall.client_address && (
                  <div style={{ marginTop: 16, padding: 16, background: '#f7fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 16 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 13, color: '#718096', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                          <MapPinIcon style={{ width: 16, height: 16 }} />
                          Service Address
                        </label>
                        <p style={{ fontSize: 15, color: '#1f2937', margin: 0 }}>
                          {selectedCall.client_address}
                          {selectedCall.client_city && selectedCall.client_state && (
                            <>, {selectedCall.client_city}, {selectedCall.client_state} {selectedCall.client_zip}</>
                          )}
                        </p>
                      </div>
                      <button 
                        onClick={() => openNavigation(selectedCall.client_address)}
                        style={{ 
                          padding: '8px 16px', 
                          borderRadius: 6,
                          border: 'none',
                          background: '#2B7FE0',
                          color: 'white',
                          fontSize: 14,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          flexShrink: 0,
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#2B7FE0'}
                      >
                        <MapPinIcon style={{ width: 16, height: 16 }} />
                        Navigate
                      </button>
                    </div>
                  </div>
                )}

                {selectedCall.description && (
                  <div style={{ marginTop: 16 }}>
                    <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Description</label>
                    <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{selectedCall.description}</p>
                  </div>
                )}
              </div>

              {/* Photos Section */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600 }}>Photos ({photos.length})</h3>
                  <label style={{
                    padding: '8px 16px',
                    background: '#2B7FE0',
                    color: 'white',
                    borderRadius: 6,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    opacity: uploading ? 0.6 : 1
                  }}>
                    {uploading ? (
                      <>
                        <CloudArrowUpIcon style={{ width: 18, height: 18 }} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <ArrowUpTrayIcon style={{ width: 18, height: 18 }} />
                        Upload Photos
                      </>
                    )}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                {photos.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', background: '#f7fafc', borderRadius: 8, border: '2px dashed #cbd5e0' }}>
                    <PhotoIcon style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#a0aec0' }} />
                    <p style={{ color: '#718096', marginBottom: 8 }}>No photos yet</p>
                    <p style={{ fontSize: 13, color: '#a0aec0' }}>Upload photos to document this service call</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                    {photos.map(photo => (
                      <div key={photo.id} style={{ position: 'relative', paddingBottom: '100%', borderRadius: 8, overflow: 'hidden', background: '#f7fafc' }}>
                        <img 
                          src={photo.photo_url} 
                          alt="Service call" 
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {(isAdmin || user?.id === photo.uploaded_by) && (
                          <button
                            onClick={() => deletePhoto(photo.id)}
                            style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              background: 'rgba(220, 38, 38, 0.9)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              padding: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <TrashIcon style={{ width: 14, height: 14 }} />
                          </button>
                        )}
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', padding: '20px 8px 8px', fontSize: 11, color: 'white' }}>
                          {photo.uploaded_by_name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer with Delete Button for Admins */}
            {isAdmin && (
              <div style={{ padding: 24, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                <button
                  onClick={() => deleteServiceCall(selectedCall.id)}
                  style={{
                    padding: '10px 20px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  <TrashIcon style={{ width: 18, height: 18 }} />
                  Delete Service Call
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#e2e8f0',
                    color: '#1f2937',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ marginBottom: 20 }}>Create Service Call</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Client *</label>
                <button
                  type="button"
                  onClick={() => setShowClientPicker(true)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 6,
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 15,
                    color: getSelectedClientName() ? '#1f2937' : '#9ca3af'
                  }}
                >
                  <span>{getSelectedClientName() || 'Select a client'}</span>
                  <MagnifyingGlassIcon style={{ width: 20, height: 20, color: '#64748b' }} />
                </button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder="Service call title"
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Service call description"
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0', minHeight: 80 }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Date *</label>
                  <input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                    required
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Time</label>
                  <input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  <UserGroupIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                  Assign Employees
                </label>
                <div style={{ 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 6, 
                  padding: 12,
                  maxHeight: 180,
                  overflowY: 'auto'
                }}>
                  {users.filter(u => u.role === 'employee' || u.role === 'admin').map(emp => (
                    <label 
                      key={emp.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: 4,
                        background: formData.assigned_employees.includes(emp.id) ? '#e0f2fe' : 'transparent',
                        marginBottom: 4,
                        transition: 'background 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.assigned_employees.includes(emp.id)}
                        onChange={() => toggleEmployeeSelection(emp.id)}
                        style={{ marginRight: 10 }}
                      />
                      <span style={{ fontWeight: formData.assigned_employees.includes(emp.id) ? 500 : 400 }}>
                        {emp.name}
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#718096' }}>
                        {emp.role}
                      </span>
                    </label>
                  ))}
                </div>
                {formData.assigned_employees.length > 0 && (
                  <p style={{ fontSize: 13, color: '#2B7FE0', marginTop: 8 }}>
                    {formData.assigned_employees.length} employee(s) selected
                  </p>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Cost ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({...formData, cost: e.target.value})}
                    placeholder="0.00"
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={!formData.client_id}
                  style={{ 
                    background: formData.client_id ? '#2B7FE0' : '#94a3b8', 
                    color: 'white', 
                    padding: '10px 20px', 
                    borderRadius: 6, 
                    border: 'none', 
                    cursor: formData.client_id ? 'pointer' : 'not-allowed' 
                  }}
                >
                  Create Service Call
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Picker Modal */}
      {showClientPicker && (
        <ClientPicker
          clients={clients}
          selectedClientId={formData.client_id}
          onSelect={(client) => setFormData({...formData, client_id: client.id})}
          onClose={() => setShowClientPicker(false)}
        />
      )}
    </div>
  );
};

export default ServiceCalls;
